import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Trash2, Scale, Eye, Users, Calendar, DollarSign,
  Globe2, Building2, MapPin, Loader2, CheckCircle2, X, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  ar: {
    title: "مقارنة التوصيات",
    subtitle: "قارن بين التوصيات المحفوظة واختر الأفضل",
    savedRecommendations: "التوصيات المحفوظة",
    noSaved: "لا توجد توصيات محفوظة",
    noSavedDesc: "احفظ توصيات من صفحة التحليل للمقارنة بينها",
    backToDashboard: "العودة للوحة التحكم",
    compare: "قارن المحدد",
    selectToCompare: "اختر 2-4 توصيات للمقارنة",
    selected: "محدد",
    delete: "حذف",
    deleteConfirm: "تم حذف التوصية",
    comparison: "المقارنة",
    budget: "الميزانية",
    reach: "الوصول المتوقع",
    leads: "العملاء المتوقعين",
    duration: "المدة",
    days: "يوم",
    aed: "درهم",
    targetCountries: "الدول المستهدفة",
    adType: "نوع الإعلان",
    createdAt: "تاريخ الحفظ",
    property: "العقار",
    bestChoice: "الأفضل",
    closeComparison: "إغلاق المقارنة"
  },
  en: {
    title: "Compare Recommendations",
    subtitle: "Compare saved recommendations and choose the best",
    savedRecommendations: "Saved Recommendations",
    noSaved: "No saved recommendations",
    noSavedDesc: "Save recommendations from the analysis page to compare them",
    backToDashboard: "Back to Dashboard",
    compare: "Compare Selected",
    selectToCompare: "Select 2-4 recommendations to compare",
    selected: "selected",
    delete: "Delete",
    deleteConfirm: "Recommendation deleted",
    comparison: "Comparison",
    budget: "Budget",
    reach: "Expected Reach",
    leads: "Expected Leads",
    duration: "Duration",
    days: "days",
    aed: "AED",
    targetCountries: "Target Countries",
    adType: "Ad Type",
    createdAt: "Saved on",
    property: "Property",
    bestChoice: "Best",
    closeComparison: "Close Comparison"
  }
};

const CompareRecommendations = ({ onBack, language = "ar" }) => {
  const [savedRecs, setSavedRecs] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);

  const t = translations[language];
  const isRTL = language === "ar";

  useEffect(() => {
    fetchSavedRecommendations();
  }, []);

  const fetchSavedRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/saved-recommendations`);
      setSavedRecs(response.data);
    } catch (error) {
      console.error("Error fetching saved recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else if (prev.length < 4) {
        return [...prev, id];
      }
      return prev;
    });
  };

  const deleteRecommendation = async (id) => {
    try {
      await axios.delete(`${API}/saved-recommendations/${id}`);
      setSavedRecs(prev => prev.filter(r => r.id !== id));
      setSelectedIds(prev => prev.filter(i => i !== id));
      toast.success(t.deleteConfirm);
    } catch (error) {
      console.error("Error deleting recommendation:", error);
    }
  };

  const compareSelected = async () => {
    if (selectedIds.length < 2) return;
    
    setComparing(true);
    try {
      const params = new URLSearchParams();
      selectedIds.forEach(id => params.append('ids', id));
      const response = await axios.post(`${API}/compare-recommendations?${params.toString()}`);
      setComparisonData(response.data);
    } catch (error) {
      console.error("Error comparing:", error);
      toast.error(language === "ar" ? "حدث خطأ في المقارنة" : "Error comparing recommendations");
    } finally {
      setComparing(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat(language === "ar" ? "ar-AE" : "en-AE").format(num);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === "ar" ? "ar-AE" : "en-AE", {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Find best values for highlighting
  const findBest = (items, key, higher = true) => {
    if (!items || items.length === 0) return -1;
    let bestIdx = 0;
    let bestVal = items[0].recommendations?.[key] || 0;
    items.forEach((item, idx) => {
      const val = item.recommendations?.[key] || 0;
      if ((higher && val > bestVal) || (!higher && val < bestVal)) {
        bestVal = val;
        bestIdx = idx;
      }
    });
    return bestIdx;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background p-4 ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
        <Button variant="outline" onClick={onBack} className="gap-2" data-testid="back-to-dashboard">
          <ChevronLeft className="w-4 h-4" />
          {t.backToDashboard}
        </Button>
      </div>

      {/* Comparison View */}
      {comparisonData && (
        <Card className="mb-6 border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-secondary/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-primary" />
                {t.comparison}
              </CardTitle>
              <CardDescription>{comparisonData.count} {language === "ar" ? "توصيات" : "recommendations"}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setComparisonData(null)}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${comparisonData.count}, 1fr)` }}>
              {comparisonData.items.map((item, idx) => {
                const bestReach = findBest(comparisonData.items, 'total_estimated_reach');
                const bestLeads = findBest(comparisonData.items, 'total_estimated_leads');
                const isBestReach = idx === bestReach;
                const isBestLeads = idx === bestLeads;
                
                return (
                  <div key={item.id} className={`p-4 rounded-lg border-2 ${isBestReach || isBestLeads ? 'border-green-500 bg-green-500/10' : 'border-border bg-muted/30'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-lg">{item.name}</h3>
                      {(isBestReach || isBestLeads) && (
                        <Badge className="bg-green-500">{t.bestChoice}</Badge>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {/* Property Info */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="w-4 h-4" />
                        <span>{language === "ar" ? item.recommendations?.emirate_name_ar : item.recommendations?.emirate_name_en}</span>
                        <span>-</span>
                        <span>{language === "ar" ? item.recommendations?.area_name_ar : item.recommendations?.area_name_en}</span>
                      </div>
                      
                      <Separator />
                      
                      {/* Budget */}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />{t.budget}
                        </span>
                        <span className="font-semibold text-secondary">
                          {formatNumber(item.budget?.total_budget_aed || 0)} {t.aed}
                        </span>
                      </div>
                      
                      {/* Reach */}
                      <div className={`flex justify-between items-center ${isBestReach ? 'text-green-500' : ''}`}>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Eye className="w-4 h-4" />{t.reach}
                        </span>
                        <span className={`font-semibold ${isBestReach ? 'text-green-500' : 'text-primary'}`}>
                          {formatNumber(item.recommendations?.total_estimated_reach || 0)}
                        </span>
                      </div>
                      
                      {/* Leads */}
                      <div className={`flex justify-between items-center ${isBestLeads ? 'text-green-500' : ''}`}>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="w-4 h-4" />{t.leads}
                        </span>
                        <span className={`font-semibold ${isBestLeads ? 'text-green-500' : 'text-cyan-500'}`}>
                          {formatNumber(item.recommendations?.total_estimated_leads || 0)}
                        </span>
                      </div>
                      
                      {/* Duration */}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-4 h-4" />{t.duration}
                        </span>
                        <span className="font-semibold">
                          {item.recommendations?.recommended_duration_days || 0} {t.days}
                        </span>
                      </div>
                      
                      <Separator />
                      
                      {/* Target Countries */}
                      <div>
                        <span className="text-muted-foreground text-sm flex items-center gap-1 mb-2">
                          <Globe2 className="w-4 h-4" />{t.targetCountries}
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {item.recommendations?.countries?.slice(0, 5).map((country, cidx) => (
                            <Badge key={cidx} variant="outline" className="text-xs">
                              {country.flag} {language === "ar" ? country.country_name_ar : country.country_name_en}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Ad Type */}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">{t.adType}</span>
                        <Badge className="bg-secondary">
                          {item.recommendations?.ad_type?.icon} {language === "ar" ? item.recommendations?.ad_type?.name_ar : item.recommendations?.ad_type?.name_en}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection Controls */}
      {savedRecs.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
          <span className="text-muted-foreground">
            {selectedIds.length} {t.selected} ({t.selectToCompare})
          </span>
          <Button 
            onClick={compareSelected} 
            disabled={selectedIds.length < 2 || comparing}
            className="gap-2 bg-gradient-to-r from-primary to-secondary"
            data-testid="compare-btn"
          >
            {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
            {t.compare}
          </Button>
        </div>
      )}

      {/* Saved Recommendations List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            {t.savedRecommendations}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {savedRecs.length === 0 ? (
            <div className="text-center py-12">
              <Scale className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground">{t.noSaved}</h3>
              <p className="text-sm text-muted-foreground/70">{t.noSavedDesc}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedRecs.map((rec) => (
                <div 
                  key={rec.id} 
                  className={`p-4 rounded-lg border-2 transition-all ${selectedIds.includes(rec.id) ? 'border-primary bg-primary/10' : 'border-border bg-muted/30 hover:border-primary/50'}`}
                  data-testid={`saved-rec-${rec.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={selectedIds.includes(rec.id)}
                      onCheckedChange={() => toggleSelection(rec.id)}
                      disabled={!selectedIds.includes(rec.id) && selectedIds.length >= 4}
                      className="mt-1"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">{rec.name}</h3>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteRecommendation(rec.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {language === "ar" ? rec.recommendations?.emirate_name_ar : rec.recommendations?.emirate_name_en}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatNumber(rec.budget?.total_budget_aed || 0)} {t.aed}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {formatNumber(rec.recommendations?.total_estimated_reach || 0)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {formatNumber(rec.recommendations?.total_estimated_leads || 0)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(rec.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {rec.recommendations?.countries?.slice(0, 4).map((country, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {country.flag}
                          </Badge>
                        ))}
                        {rec.recommendations?.countries?.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{rec.recommendations.countries.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompareRecommendations;
